/* A SCUTTLE, not a squeeze.
 *
 * The bramble shipped as two frames: `S.bramble_0`, and `S.bramble_1`, which
 * was `bobbed()` — the whole picture redrawn one row lower, the mid-band
 * pushed out, the middle pair of legs tucked. Alternated forever, the only
 * thing that ever changes is that drop, so the thing reads as being pinched
 * rather than walking anywhere.
 *
 * Both are kept EXACTLY, at phase 0 and phase 4. The six between them exist to
 * SPEND the drop rather than pay for it all at once.
 *
 * THE DROP IS THE WHOLE MOTION BUDGET. Fourteen rows of thorn outline moved
 * down one row is 112 changed pixels — two thirds of the ink in the sprite —
 * and no amount of leg-tucking and thorn-rattling elsewhere comes near it. An
 * earlier pass laid the drop out as a single step between phase 2 and phase 3
 * and another back between 5 and 6, and measured 22/15/112/33/33/111/15/22:
 * two transitions carrying five times what the other six carried. That is not
 * a bob, it is a cut. The creature teleports down, sits there for three frames
 * and teleports back, which is the two-pose failure the eight frames exist to
 * fix, spelled at greater length.
 *
 * So the drop is CUT AT THE NECK and paid over two frames in each direction —
 * which is also what a body taking weight actually does:
 *
 *   1 -> 2  the head and thorax come down onto planted feet. The plain row
 *           between the eyes and the ridge is eaten by the squat; the ground
 *           line does not move. 14 rows tall, then 13.
 *   2 -> 3  the skirt and the legs follow it down and the eaten row grows
 *           back. The whole picture is now one row low — phase 4's position.
 *   5 -> 6  the legs gather back under the body FIRST: feet up, head still
 *           low. The same squat, entered from the other side.
 *   6 -> 7  the body lifts off them and the row comes back.
 *
 * Steps are now 22/58/74/33/33/74/55/22 — biggest 1.3x the median, against a
 * budget of 3x. The squat is one row shorter than the other seven frames, the
 * way `sporeling` is (15/15/14/14/13/13/15/15); nothing is ever TALLER than
 * the shipped art, so the silhouette never bloats.
 *
 * Three more things move, on three different rhythms, which is what stops
 * eight frames from being two frames with padding:
 *
 *   the flanks   the hips (the pinched row under the ridge) spread first, on
 *                the way down, because they take the load first; both hips and
 *                cheeks are out at 3 and 5; phase 4 is the shipped spread,
 *                which pushes the whole mid-band out; only the cheeks are
 *                still out at 6, on the way back up. That staggering is why
 *                phase 2 and phase 6 — the two squats — are not the same frame
 *                with different legs: the mass is in a different place.
 *   the legs     six tips on the ground row, and a lifted leg is simply gone
 *                from it, tucked under the body. That vocabulary is phase 4's
 *                own: it lifts the middle pair. So the lift is made a WAVE
 *                that travels along the row — x2, x4, x6, then (x6,x9) at
 *                phase 4, then x9, x11, x13 — and phase 4 falls into the
 *                middle of it without being touched. One side runs its three
 *                legs while the other is planted, and picks up four phases
 *                later: a metachronal wave, half a cycle out of phase side to
 *                side. Only at phase 4 — the shipped frame, the handover from
 *                one side to the other — are two feet off the ground at once,
 *                and they are the two inner ones. When an OUTER leg is up its
 *                knee goes with it (phases 1 and 7); the inner knees
 *                are the body's own bottom corners and stay where they are.
 *   the crown    the four thorns rattle at roughly twice the leg rate, and
 *                never sit still two frames running: lean, splay, lean, centre
 *                on the way down; lean, gather, lean, centre on the way up.
 *                Thorns are stiff and light — they are the part of this animal
 *                that would chatter. Only the two rows above the crown line
 *                move; the roots and the eyes hold, because the face is what a
 *                player actually tracks across the frames.
 *
 * Phase 7 has the last leg of the wave up and the crown still trailing; phase
 * 0 plants it and centres the crown, at the same height. The seam is a step,
 * not a cut.
 */

export const bramble_0 = [
  '..o..o....o..o..',
  '..oGo.o..o.oGo..',
  '.oGGGoGooGoGGGo.',
  'oGGGGGGGGGGGGGGo',
  '.oGGgGGGGGGgGGo.',
  '..oGGgeGGegGGo..',
  '.oGGGGGGGGGGGGo.',
  'oGGGgggggggGGGGo',
  '.oGGGGGGGGGGGGo.',
  '..oGgGGGGGGgGo..',
  '.oGGGGGGGGGGGGo.',
  'o.oGGGGGGGGGGo.o',
  '.o..oGGGGGGo..o.',
  '..o.o.o..o.o.o..',
  '................',
  '................',
];

// The wave starts: outer-left leg and its knee come up, crown leans after it.
export const bramble_1 = [
  '.o..o....o..o...',
  '.oGo.o..o.oGo...',
  '.oGGGoGooGoGGGo.',
  'oGGGGGGGGGGGGGGo',
  '.oGGgGGGGGGgGGo.',
  '..oGGgeGGegGGo..',
  '.oGGGGGGGGGGGGo.',
  'oGGGgggggggGGGGo',
  '.oGGGGGGGGGGGGo.',
  '..oGgGGGGGGgGo..',
  '.oGGGGGGGGGGGGo.',
  'o.oGGGGGGGGGGo.o',
  '....oGGGGGGo..o.',
  '....o.o..o.o.o..',
  '................',
  '................',
];

// The squat. Head and thorax drop a row onto feet that do not move — the row
// between the eyes and the ridge is what gives. The hips spread because the
// load has arrived there; the crown throws open.
export const bramble_2 = [
  '................',
  '.o..o......o..o.',
  '.oGo...oo...oGo.',
  '.oGGGoGooGoGGGo.',
  'oGGGGGGGGGGGGGGo',
  '.oGGgGGGGGGgGGo.',
  '..oGGgeGGegGGo..',
  'oGGGgggggggGGGGo',
  '.oGGGGGGGGGGGGo.',
  '.ooGgGGGGGGgGoo.',
  '.oGGGGGGGGGGGGo.',
  'o.oGGGGGGGGGGo.o',
  '.o..oGGGGGGo..o.',
  '..o...o..o.o.o..',
  '................',
  '................',
];

// The rest follows the head down: skirt, knees and feet all drop the row, and
// the eaten body row comes back. Cheeks out too now; the middle-left leg is
// off the ground, which is where phase 4 picks the wave up.
export const bramble_3 = [
  '................',
  '.o..o....o..o...',
  '.oGo.o..o.oGo...',
  '.oGGGoGooGoGGGo.',
  'oGGGGGGGGGGGGGGo',
  '.oGGgGGGGGGgGGo.',
  '.ooGGgeGGegGGoo.',
  '.oGGGGGGGGGGGGo.',
  'oGGGgggggggGGGGo',
  '.oGGGGGGGGGGGGo.',
  '.ooGgGGGGGGgGoo.',
  '.oGGGGGGGGGGGGo.',
  'o.oGGGGGGGGGGo.o',
  '.o..oGGGGGGo..o.',
  '..o.o....o.o.o..',
  '................',
];

export const bramble_4 = [
  '................',
  '..o..o....o..o..',
  '..oGo.o..o.oGo..',
  '.oGGGoGooGoGGGo.',
  '.GGGGGGGGGGGGGG.',
  '..GGgGGGGGGgGG..',
  '.ooGGgeGGegGGoo.',
  'ooGGGGGGGGGGGGoo',
  '.GGGgggggggGGGG.',
  'ooGGGGGGGGGGGGoo',
  '.ooGgGGGGGGgGoo.',
  'ooGGGGGGGGGGGGoo',
  'o.oGGGGGGGGGGo.o',
  '.o..oGGGGGGo..o.',
  '..o.o......o.o..',
  '................',
];

// Push-off. Middle-left plants, middle-right is up: the wave has crossed the
// centre line. Still low, crown thrown the other way.
export const bramble_5 = [
  '................',
  '...o..o....o..o.',
  '...oGo.o..o.oGo.',
  '.oGGGoGooGoGGGo.',
  'oGGGGGGGGGGGGGGo',
  '.oGGgGGGGGGgGGo.',
  '.ooGGgeGGegGGoo.',
  '.oGGGGGGGGGGGGo.',
  'oGGGgggggggGGGGo',
  '.oGGGGGGGGGGGGo.',
  '.ooGgGGGGGGgGoo.',
  '.oGGGGGGGGGGGGo.',
  'o.oGGGGGGGGGGo.o',
  '.o..oGGGGGGo..o.',
  '..o.o.o....o.o..',
  '................',
];

// The squat again, entered feet-first: the legs gather back under a body that
// is still low, so the ground line rises before the head does. Weight is
// coming off the hips — only the cheeks are still spread — and the thorns
// pull in. Inner-right is the lifted leg.
export const bramble_6 = [
  '................',
  '...o..o..o..o...',
  '...oGoo..ooGo...',
  '.oGGGoGooGoGGGo.',
  'oGGGGGGGGGGGGGGo',
  '.oGGgGGGGGGgGGo.',
  '.ooGGgeGGegGGoo.',
  'oGGGgggggggGGGGo',
  '.oGGGGGGGGGGGGo.',
  '..oGgGGGGGGgGo..',
  '.oGGGGGGGGGGGGo.',
  'o.oGGGGGGGGGGo.o',
  '.o..oGGGGGGo..o.',
  '..o.o.o..o...o..',
  '................',
  '................',
];

// Full extension: the body lifts off the gathered legs and stands its row
// back up. The last leg of the wave — outer-right, knee and all — is still
// off the ground. Phase 0 plants it and re-centres the crown.
export const bramble_7 = [
  '...o..o....o..o.',
  '...oGo.o..o.oGo.',
  '.oGGGoGooGoGGGo.',
  'oGGGGGGGGGGGGGGo',
  '.oGGgGGGGGGgGGo.',
  '..oGGgeGGegGGo..',
  '.oGGGGGGGGGGGGo.',
  'oGGGgggggggGGGGo',
  '.oGGGGGGGGGGGGo.',
  '..oGgGGGGGGgGo..',
  '.oGGGGGGGGGGGGo.',
  'o.oGGGGGGGGGGo.o',
  '.o..oGGGGGGo....',
  '..o.o.o..o.o....',
  '................',
  '................',
];
